import React, { useMemo, useState, useEffect, useCallback } from "react";
import PropTypes from "prop-types";
import UserFilter from "./user-filter";

import { getRoles, updateEmployeeApplicationRoles } from "../../../api/roles";
import api from "../../../api/api";
import AssignRoleDialog from "../components/AssignRoleDialog";
import "./user-list.css";

import EditIcon from "../../../assets/editIcon.svg";
import MUIBoxTable from "../components/mui-box-table";
import { Box, useTheme, useMediaQuery, Snackbar, Alert } from "@mui/material";
import { useDispatch } from "react-redux";
import { setFilters as setMembersFilters } from "../../../redux/slices/membersFilterSlice";
import { useLoading } from "../../../context/LoadingContext";

const UserList = ({ canWrite }) => {
  const [loading] = React.useState(false);
  const [users, setUsers] = useState([]);
  const [searchText, setSearchText] = React.useState("");
  const [role, setRole] = useState([]);
  const [open, setOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [roles, setRoles] = useState([]);
  const [dialogType, setDialogType] = useState("assign");
  const [isFetch, setIsFetch] = useState(false);
  const { showLoading, hideLoading } = useLoading();
  const [pageIndex, setPageIndex] = useState(0);

  const [totalRows, setTotalRows] = useState(0);
  const [error, setError] = useState(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const dispatch = useDispatch();

  const handleOpen = (user, type) => {
    setSelectedUser(user);
    setDialogType(type);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setSelectedUser(null);
  };

  const headers = [
    { key: "employeeFullName", label: "User Name" },
    { key: "emailId", label: "Email" },
    { key: "departmentName", label: "Department Name" },
    { key: "status", label: "Status" },
    {
      key: "roleName",
      label: "Role",
      render: (user) => {
        let roleContent;

        if (user.roles && user.roles.length > 0) {
          roleContent = user.roles
            .map((role) =>
              role.roleName === "Super Admin" ? "Admin" : role.roleName,
            )
            .join(", ");
        } else if (canWrite) {
          roleContent = (
            <button
              className="assign-role-button"
              onClick={() => handleOpen(user, "assign")}
            >
              Assign Role
            </button>
          );
        } else {
          roleContent = "-";
        }

        return roleContent;
      },
    },
    {
      key: "edit",
      label: "Edit",
      render: (user) =>
        canWrite && user.roles?.length > 0 ? (
          <button
            type="button"
            aria-label="Edit user"
            className="user-list-edit-icon"
            style={{
              background: "none",
              border: "none",
              padding: 0,
              cursor: "pointer",
            }}
            tabIndex={0}
            onClick={() => handleOpen(user, "edit")}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                handleOpen(user, "edit");
              }
            }}
          >
            <img
              src={EditIcon}
              alt="Edit"
              style={{ width: 18, height: 18 }}
              aria-hidden="true"
            />
          </button>
        ) : (
          "-"
        ),
    },
  ];

  const handleAssignRoleSubmit = async (values) => {
    showLoading();
    try {
      const employeeId = values.employeeId;
      const applicationId = 2;
      let roleIds = [];

      if (Array.isArray(values.allRoleIds)) {
        roleIds = values.allRoleIds;
      } else if (values.roleId) {
        roleIds = [values.roleId];
      } else {
        roleIds = [];
      }

      if (roleIds.length === 0) {
        handleClose();
        setIsFetch(!isFetch);
        return;
      }

      await updateEmployeeApplicationRoles(employeeId, applicationId, {
        roleIds,
      });

      dispatch(
        setMembersFilters({
          assigneeFilter: [],
          selectedAssigneeIds: [],
          assigneeFilterSearch: "",
          assignees: [],
          assigneeNameToIdMap: {},
        }),
      );
      handleClose();
      setIsFetch(!isFetch);
    } catch (err) {
      setError(
        "Failed to assign roles. Please try again.",
        err?.message,
        error,
      );
    } finally {
      hideLoading();
    }
  };

  const fetchUsers = useCallback(
    async () => {
      showLoading();
      try {
        const params = {
          ...(searchText ? { search: searchText } : {}),
        };
        const res = await api.get("/api/employees", { params });
        const data = res?.data ?? res;
        const list = Array.isArray(data) ? data : (data?.content ?? []);
        const total = Array.isArray(data)
          ? list.length
          : (data?.totalElements ?? list.length ?? 0);

        const normalized = list.map((e) => ({
          id: e.id,
          employeeFullName: `${e.firstName ?? ""} ${e.lastName ?? ""}`.trim(),
          emailId: e.emailId,
          departmentName: e.department?.departmentName || "-",
          status: e.status === 1 ? "Enable" : "Disable",
          roles: (e.roles || []).filter(
            (r) => (r.roleName || r.name || "").toUpperCase() !== "L0",
          ),
        }));
        setUsers(normalized);
        setTotalRows(total);
      } catch (err) {
        setError("Failed to fetch users.", err?.message);
      } finally {
        hideLoading();
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [showLoading, hideLoading, searchText, pageIndex],
  );

  const fetchRoles = async () => {
    try {
      const res = await getRoles();
      const raw = res?.data ?? res;
      const list = Array.isArray(raw) ? raw : (raw?.data ?? []);

      const mapped = (Array.isArray(list) ? list : [])
        .filter((r) => (r.roleName || r.name || "").toUpperCase() !== "L0")
        .map((r) => ({
          id: r.roleId,
          name: r.roleName,
        }));
      setRoles(mapped);
    } catch (err) {
      setError("Failed to fetch roles.", err?.message);
      setRoles([]);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers, isFetch]);

  useEffect(
    () => {
      void fetchRoles();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );
  const noResultText = useMemo(() => {
    if (loading) {
      return "Loading List Of Users...";
    }
    if (searchText) {
      return "No users found matching your search";
    }
    return "No users available";
  }, [loading, searchText]);

  const filteredUsers = useMemo(() => {
    if (!Array.isArray(role) || role.length === 0) {
      return users;
    }
    return users.filter((user) =>
      user.roles.some((userRole) => role.includes(userRole.roleName)),
    );
  }, [users, role]);

  const [toastMessage, setToastMessage] = useState("");

  const handleUserAdded = () => {
    setToastMessage("User created and role assigned successfully.");
    fetchUsers();
  };

  return (
    <>
      <div className="user-list-table-container">
        <UserFilter
          role={role}
          setRole={setRole}
          searchText={searchText}
          onSearch={(text) => {
            setSearchText(text);
            setPageIndex(0);
          }}
          roles={roles}
          canWrite={canWrite}
          existingUsers={users}
          onUserAdded={handleUserAdded}
        />
        <MUIBoxTable
          columns={headers}
          data={filteredUsers}
          noResultText={noResultText}
          tableHeight={"calc(100dvh - 240px)"}
        />
      </div>
      <Snackbar
        open={Boolean(toastMessage)}
        autoHideDuration={4000}
        onClose={() => setToastMessage("")}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={() => setToastMessage("")}
          severity="success"
          sx={{ width: "100%", fontWeight: 500 }}
        >
          {toastMessage}
        </Alert>
      </Snackbar>
      {totalRows > 0 && (
        <Box
          sx={{
            position: "fixed",
            bottom: 0,
            left: isMobile ? 0 : "260px",
            right: 0,
            backgroundColor: "#F7F8FB",
            borderTop: "1px solid #E0E0E0",
            display: "flex",
            justifyContent: "flex-end",
            px: 2,
            py: 1,
            zIndex: 1000,
          }}
        ></Box>
      )}
      {open && (
        <AssignRoleDialog
          open={open}
          onClose={handleClose}
          user={selectedUser}
          roles={roles}
          type={dialogType}
          onSubmit={handleAssignRoleSubmit}
        />
      )}
    </>
  );
};

UserList.propTypes = {
  canWrite: PropTypes.bool.isRequired,
};

export default UserList;
